import { useCallback, useRef } from "react";
import type { EngineClient } from "./engineClient";
import { useEngineClient } from "./engineClient";
import { useRootStore } from "../stores/hooks";
import type { RootStore } from "../stores";

const WORKSPACE_KEY = "*";

const loadSolution = async (
  store: RootStore,
  engineClient: EngineClient,
  inFlight: Map<string, Promise<void>>,
  solutionId: string,
): Promise<void> => {
  if (store.members.hasSolution(solutionId)) {
    return;
  }

  const pending = inFlight.get(solutionId);
  if (pending !== undefined) {
    await pending;
    return;
  }

  const request = engineClient
    .listSolutionTeamsAndMembers({ solutionId })
    .then(({ teams, members }) => {
      store.members.setForSolution(solutionId, members);
      store.teams.setForSolution(solutionId, teams);
    })
    .finally(() => {
      inFlight.delete(solutionId);
    });

  inFlight.set(solutionId, request);

  await request;
};

const loadWorkspaceDirectory = async (
  store: RootStore,
  engineClient: EngineClient,
  inFlight: Map<string, Promise<void>>,
): Promise<void> => {
  if (store.members.workspaceLoaded) {
    return;
  }

  const pending = inFlight.get(WORKSPACE_KEY);
  if (pending !== undefined) {
    await pending;
    return;
  }

  const request = Promise.all([
    engineClient.listMembers({}),
    engineClient.listTeams({}),
  ])
    .then(([{ members }, { teams }]) => {
      store.members.setWorkspace(members);
      store.teams.setWorkspace(teams);
    })
    .finally(() => {
      inFlight.delete(WORKSPACE_KEY);
    });

  inFlight.set(WORKSPACE_KEY, request);

  await request;
};

/**
 * Loads the members and teams the automation builder can offer.
 *
 * Two different scopes are needed, because two different questions are being
 * asked. A record's user field (Assigned To) may only hold members who can see
 * that record, so it is scoped to the solution the action writes into -- which
 * is not necessarily the solution hosting the automation. A notification
 * recipient (Send To, CC) is addressed directly and needs no record access, so
 * it is scoped to the whole account.
 *
 * Each scope is fetched at most once per opened builder (the stores are reset by
 * `RootStore.init`), and concurrent requests for the same scope share one call.
 */
export const useSolutionDirectory = (): {
  loadForSolutions: (solutionIds: readonly string[]) => Promise<void>;
  loadWorkspace: () => Promise<void>;
} => {
  const store = useRootStore();
  const engineClient = useEngineClient();
  const inFlight = useRef(new Map<string, Promise<void>>());

  const loadForSolutions = useCallback(
    async (solutionIds: readonly string[]): Promise<void> => {
      const unique = [...new Set(solutionIds)].filter((solutionId) =>
        Boolean(solutionId),
      );

      await Promise.all(
        unique.map(
          async (solutionId) =>
            await loadSolution(
              store,
              engineClient,
              inFlight.current,
              solutionId,
            ),
        ),
      );
    },
    [store, engineClient],
  );

  const loadWorkspace = useCallback(
    async (): Promise<void> =>
      await loadWorkspaceDirectory(store, engineClient, inFlight.current),
    [store, engineClient],
  );

  return { loadForSolutions, loadWorkspace };
};
